import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Star, Eye, EyeOff, Trash2, Edit2 } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    quote: '', author_name: '', author_title: '', company_name: '',
    author_image: '', rating: 5, featured: false, status: 'draft'
  });

  const fetchData = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/cms/testimonials`, { params, withCredentials: true });
      setTestimonials(response.data);
    } catch (error) {
      toast.error("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/cms/testimonials/${editingItem.testimonial_id}`, formData, { withCredentials: true });
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${API}/cms/testimonials`, formData, { withCredentials: true });
        toast.success("Testimonial created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save testimonial");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      quote: item.quote || '', author_name: item.author_name || '',
      author_title: item.author_title || '', company_name: item.company_name || '',
      author_image: item.author_image || '', rating: item.rating || 5,
      featured: item.featured || false, status: item.status || 'draft'
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/cms/testimonials/${id}`, { withCredentials: true });
      toast.success("Testimonial deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/cms/testimonials/${item.testimonial_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Testimonial ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await axios.put(`${API}/cms/testimonials/${item.testimonial_id}`, { featured: !item.featured }, { withCredentials: true });
      toast.success(item.featured ? 'Removed from featured' : 'Marked as featured');
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ quote: '', author_name: '', author_title: '', company_name: '',
      author_image: '', rating: 5, featured: false, status: 'draft' });
  };

  const statusColors = {
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  return (
    <AdminLayout title="Testimonials">
      <div className="space-y-6" data-testid="admin-testimonials">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Testimonials</h2>
            <p className="text-sm text-[#8A8A8A]">Customer testimonials for public website</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Testimonial
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit' : 'New'} Testimonial</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <textarea placeholder="Quote *" required rows={4} value={formData.quote}
                  onChange={(e) => setFormData({...formData, quote: e.target.value})} className="input-field resize-none" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Author Name *" required value={formData.author_name}
                    onChange={(e) => setFormData({...formData, author_name: e.target.value})} className="input-field" />
                  <input type="text" placeholder="Author Title" value={formData.author_title}
                    onChange={(e) => setFormData({...formData, author_title: e.target.value})} className="input-field" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Company Name" value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})} className="input-field" />
                  <input type="url" placeholder="Author Image URL" value={formData.author_image}
                    onChange={(e) => setFormData({...formData, author_image: e.target.value})} className="input-field" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#8A8A8A] mb-1">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button key={star} type="button" onClick={() => setFormData({...formData, rating: star})}
                          className={`p-1 ${star <= formData.rating ? 'text-[#5F8FB8]' : 'text-[#2E2E2E]'}`}>
                          <Star className="w-6 h-6 fill-current" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Featured testimonial (shown prominently)</span>
                </label>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingItem ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-4">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-48">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] h-40 animate-pulse" />
            ))
          ) : testimonials.length === 0 ? (
            <div className="col-span-full text-center py-12 text-[#8A8A8A] border border-[#2E2E2E]">
              No testimonials found
            </div>
          ) : (
            testimonials.map((item) => (
              <div key={item.testimonial_id} className={`bg-[#1C1C1C] border p-6 ${item.featured ? 'border-[#5F8FB8]/50' : 'border-[#2E2E2E]'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {item.featured && (
                      <div className="text-xs text-[#5F8FB8] uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" /> Featured
                      </div>
                    )}
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < item.rating ? 'text-[#5F8FB8] fill-current' : 'text-[#2E2E2E]'}`} />
                      ))}
                    </div>
                    <p className="text-sm italic text-[#F5F4EF] mb-4">&ldquo;{item.quote}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      {item.author_image && (
                        <img src={item.author_image} alt={item.author_name} className="w-10 h-10 rounded-full object-cover" />
                      )}
                      <div>
                        <div className="font-medium text-sm">{item.author_name}</div>
                        <div className="text-xs text-[#8A8A8A]">
                          {item.author_title}{item.company_name && `, ${item.company_name}`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className={`px-2 py-0.5 text-xs border ${statusColors[item.status]}`}>{item.status}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                        <DropdownMenuItem onClick={() => handleEdit(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleStatus(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                          {item.status === 'published' ? <><EyeOff className="w-4 h-4 mr-2" /> Unpublish</> : <><Eye className="w-4 h-4 mr-2" /> Publish</>}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleFeatured(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                          <Star className="w-4 h-4 mr-2" /> {item.featured ? 'Remove Featured' : 'Mark Featured'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(item.testimonial_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Testimonials;
